/**
 * Particle Model & States Lab — deterministic model and reviewed data.
 *
 * Owns the particle arrangement and motion of solids, liquids and gases, the heating curve of
 * water, diffusion and compression comparisons, density data and the prediction cases. The page
 * renders only what these functions return (study-chemistry-tools-prd.md §8.1 `particle-model`).
 */

export type ParticleMode = 'states' | 'explain';
export type MatterState = 'solid' | 'liquid' | 'gas';
export type HeatingPhase = 'solid' | 'melting' | 'liquid' | 'boiling' | 'gas';

/** Stable identity used by reviewed CurriculumToolBinding records. */
export const PARTICLE_MODEL_TOOL_IDENTITY = {
  id: 'particle-model',
  toolKey: 'chemistry-particle-model',
  toolVersion: '1.0.0',
  computation: 'client-deterministic',
  evidencePolicy: 'none',
  masteryPolicy: 'not-assessed',
  curriculumPolicy: 'reviewed-binding-required',
} as const;

export const PARTICLE_MODEL_MODES = [
  { mode: 'states', fromYear: 7, label: 'Year 7 · States' },
  { mode: 'explain', fromYear: 8, label: 'Year 8 · Explain' },
] as const satisfies readonly { mode: ParticleMode; fromYear: number; label: string }[];

export const PARTICLE_REVIEW_STATUS = 'pending-teaching-review' as const;

/** Fixed-decimal number in Australian English formatting. */
export function formatNumber(value: number, digits = 1): string {
  return value.toLocaleString('en-AU', { minimumFractionDigits: digits, maximumFractionDigits: digits });
}

export interface DataSource {
  id: string;
  citation: string;
}

export const PARTICLE_MODEL_SOURCES: DataSource[] = [
  { id: 'crc-density', citation: 'CRC Handbook of Chemistry and Physics (97th ed.) — densities of the elements, water, ice, ethanol, glycerol and mercury near 20 °C and 101.325 kPa.' },
  { id: 'nist-water', citation: 'NIST Chemistry WebBook, SRD 69 — water: melting point 0 °C and normal boiling point 100 °C at 101.325 kPa; saturated steam density at 100 °C; textbook heat capacities and enthalpies of fusion and vaporisation.' },
  { id: 'standard-air', citation: 'US Standard Atmosphere (1976) — dry air density 1.204 kg/m³ at 20 °C and 101.325 kPa.' },
  { id: 'crc-viscosity', citation: 'CRC Handbook of Chemistry and Physics — viscosity of liquid water from 0 °C to 100 °C at 101.325 kPa.' },
  { id: 'crc-diffusion', citation: 'CRC Handbook of Chemistry and Physics — typical diffusion coefficients: small gas molecules in air about 10⁻⁵ m²/s, small molecules in water about 10⁻⁹ m²/s, near 25 °C.' },
  { id: 'crc-compressibility', citation: 'CRC Handbook of Chemistry and Physics — isothermal compressibility of water about 4.6 × 10⁻¹⁰ Pa⁻¹ at 20 °C; bulk modulus of iron about 170 GPa.' },
];

// States of matter

export interface StateProperties {
  state: MatterState;
  label: string;
  arrangement: string;
  motion: string;
  attraction: string;
  shapeAndVolume: string;
  compressibility: string;
}

export const STATE_PROPERTIES: Record<MatterState, StateProperties> = {
  solid: {
    state: 'solid',
    label: 'Solid',
    arrangement: 'Packed closely in a fixed, regular pattern.',
    motion: 'Vibrate on the spot; they do not move past one another.',
    attraction: 'Strong attractions hold each particle in place.',
    shapeAndVolume: 'Fixed shape and fixed volume.',
    compressibility: 'Almost cannot be compressed: particles already touch.',
  },
  liquid: {
    state: 'liquid',
    label: 'Liquid',
    arrangement: 'Close together but in an irregular arrangement.',
    motion: 'Move around and slide past one another.',
    attraction: 'Attractions are still strong enough to keep particles together.',
    shapeAndVolume: 'Takes the shape of its container but keeps its volume.',
    compressibility: 'Almost cannot be compressed: particles are still close together.',
  },
  gas: {
    state: 'gas',
    label: 'Gas',
    arrangement: 'Far apart, with large empty spaces between particles.',
    motion: 'Move quickly in straight lines in all directions until they collide.',
    attraction: 'Attractions are very weak compared with the particles’ motion.',
    shapeAndVolume: 'Spreads out to fill the whole container.',
    compressibility: 'Easy to compress: the spaces between particles can shrink.',
  },
};

// Heating curve of 1 g of water at 101.325 kPa

export const WATER_HEATING = {
  sampleMassG: 1,
  pressureKPa: 101.325,
  startTemperatureC: -20,
  meltingPointC: 0,
  boilingPointC: 100,
  endTemperatureC: 120,
  iceHeatCapacityJPerGK: 2.09,
  waterHeatCapacityJPerGK: 4.18,
  steamHeatCapacityJPerGK: 2.01,
  fusionEnergyJPerG: 334,
  vaporisationEnergyJPerG: 2260,
  sourceId: 'nist-water',
  conditions: '1 g of pure water heated steadily at 101.325 kPa (sea-level pressure).',
  reviewStatus: PARTICLE_REVIEW_STATUS,
} as const;

export interface HeatingSegment {
  phase: HeatingPhase;
  startEnergyJ: number;
  endEnergyJ: number;
  startTemperatureC: number;
  endTemperatureC: number;
}

export interface StateFractions {
  solid: number;
  liquid: number;
  gas: number;
}

export interface HeatingState {
  energyJ: number;
  temperatureC: number;
  phase: HeatingPhase;
  fractions: StateFractions;
  /** True while the temperature stays constant during a change of state. */
  plateau: boolean;
}

export function buildHeatingSegments(): HeatingSegment[] {
  const w = WATER_HEATING;
  const iceEnergy = (w.meltingPointC - w.startTemperatureC) * w.iceHeatCapacityJPerGK * w.sampleMassG;
  const meltEnergy = w.fusionEnergyJPerG * w.sampleMassG;
  const waterEnergy = (w.boilingPointC - w.meltingPointC) * w.waterHeatCapacityJPerGK * w.sampleMassG;
  const boilEnergy = w.vaporisationEnergyJPerG * w.sampleMassG;
  const steamEnergy = (w.endTemperatureC - w.boilingPointC) * w.steamHeatCapacityJPerGK * w.sampleMassG;
  const plan: Array<[HeatingPhase, number, number, number]> = [
    ['solid', iceEnergy, w.startTemperatureC, w.meltingPointC],
    ['melting', meltEnergy, w.meltingPointC, w.meltingPointC],
    ['liquid', waterEnergy, w.meltingPointC, w.boilingPointC],
    ['boiling', boilEnergy, w.boilingPointC, w.boilingPointC],
    ['gas', steamEnergy, w.boilingPointC, w.endTemperatureC],
  ];
  let start = 0;
  return plan.map(([phase, energy, startTemperatureC, endTemperatureC]) => {
    const segment = { phase, startEnergyJ: start, endEnergyJ: start + energy, startTemperatureC, endTemperatureC };
    start += energy;
    return segment;
  });
}

export const HEATING_SEGMENTS = buildHeatingSegments();
export const HEATING_TOTAL_ENERGY_J = HEATING_SEGMENTS[HEATING_SEGMENTS.length - 1].endEnergyJ;

function fractionsFor(phase: HeatingPhase, progress: number): StateFractions {
  switch (phase) {
    case 'solid':
      return { solid: 1, liquid: 0, gas: 0 };
    case 'melting':
      return { solid: 1 - progress, liquid: progress, gas: 0 };
    case 'liquid':
      return { solid: 0, liquid: 1, gas: 0 };
    case 'boiling':
      return { solid: 0, liquid: 1 - progress, gas: progress };
    default:
      return { solid: 0, liquid: 0, gas: 1 };
  }
}

/** Temperature and state after adding `energyJ` to 1 g of ice at −20 °C. Null outside the model. */
export function heatingStateAt(energyJ: number): HeatingState | null {
  if (!Number.isFinite(energyJ) || energyJ < 0 || energyJ > HEATING_TOTAL_ENERGY_J) return null;
  const segment =
    HEATING_SEGMENTS.find((candidate) => energyJ <= candidate.endEnergyJ) ??
    HEATING_SEGMENTS[HEATING_SEGMENTS.length - 1];
  const width = segment.endEnergyJ - segment.startEnergyJ;
  const progress = width === 0 ? 1 : (energyJ - segment.startEnergyJ) / width;
  const temperatureC =
    segment.startTemperatureC + (segment.endTemperatureC - segment.startTemperatureC) * progress;
  const plateau = segment.phase === 'melting' || segment.phase === 'boiling';
  return { energyJ, temperatureC, phase: segment.phase, fractions: fractionsFor(segment.phase, progress), plateau };
}

/** Energy (J) used so far to overcome attractions on the melting and boiling plateaus. */
export function energyToAttractionsJ(energyJ: number): number | null {
  if (!heatingStateAt(energyJ)) return null;
  return HEATING_SEGMENTS.filter((segment) => segment.phase === 'melting' || segment.phase === 'boiling').reduce(
    (sum, segment) =>
      sum + Math.min(Math.max(energyJ - segment.startEnergyJ, 0), segment.endEnergyJ - segment.startEnergyJ),
    0,
  );
}

/** Energy at a fraction (0–1) of the way through one heating segment. */
export function energyWithinPhase(phase: HeatingPhase, fraction: number): number {
  const segment = HEATING_SEGMENTS.find((candidate) => candidate.phase === phase);
  if (!segment || fraction < 0 || fraction > 1) throw new Error(`No heating segment for ${phase} at ${fraction}`);
  return segment.startEnergyJ + (segment.endEnergyJ - segment.startEnergyJ) * fraction;
}

export const HEATING_PHASE_LABELS: Record<HeatingPhase, string> = {
  solid: 'Ice warming',
  melting: 'Melting at 0 °C',
  liquid: 'Liquid water warming',
  boiling: 'Boiling at 100 °C',
  gas: 'Steam warming',
};

/** What a learner would see in the beaker at each heating phase. */
export const WATER_PHASE_NAMES: Record<HeatingPhase, string> = {
  solid: 'Ice',
  melting: 'Ice and liquid water',
  liquid: 'Liquid water',
  boiling: 'Liquid water and steam',
  gas: 'Steam',
};

export const HEATING_PHASE_ENERGY_NOTES: Record<HeatingPhase, string> = {
  solid: 'Energy makes the particles vibrate faster, so the temperature rises.',
  melting:
    'Energy is used to overcome attractions so particles can leave their fixed places. The temperature stays at 0 °C.',
  liquid: 'Energy makes the particles move faster, so the temperature rises.',
  boiling:
    'Energy is used to overcome the attractions between particles so they can separate into a gas. The temperature stays at 100 °C.',
  gas: 'Energy makes the gas particles move faster, so the temperature rises.',
};

// Particle layout and motion (generated from model state; never drawn by hand)

export const PARTICLE_BOX = { width: 100, height: 70 } as const;
export const DEFAULT_PARTICLE_COUNT = 36;
export const KELVIN_OFFSET = 273.15;
const REFERENCE_TEMPERATURE_K = 293.15;
const LATTICE_SPACING = 6.4;
const LATTICE_COLUMNS = 6;
const LIQUID_SPACING = 6.6;
const BOX_MARGIN = 4;
const SOLID_VIBRATION_AMPLITUDE = 0.55;
const LIQUID_WOBBLE_AMPLITUDE = 1.1;
const SOLID_VIBRATION_HZ = 2.6;
const LIQUID_DRIFT_SPEED = 2.4;
const GAS_SPEED = 16;
const GOLDEN_ANGLE = 2.399963;

export interface ModelParticle {
  id: number;
  state: MatterState;
  homeX: number;
  homeY: number;
  /** Unit direction of travel for liquid drift and gas flight. */
  directionX: number;
  directionY: number;
  phaseOffset: number;
}

export interface ParticlePoint {
  id: number;
  state: MatterState;
  x: number;
  y: number;
}

/** Deterministic pseudo-random sequence so the same model state always draws the same picture. */
function seededRandom(seed: number): () => number {
  let value = seed >>> 0;
  return () => {
    value = (value + 0x6d2b79f5) >>> 0;
    let mixed = value;
    mixed = Math.imul(mixed ^ (mixed >>> 15), mixed | 1);
    mixed ^= mixed + Math.imul(mixed ^ (mixed >>> 7), mixed | 61);
    return ((mixed ^ (mixed >>> 14)) >>> 0) / 4294967296;
  };
}

/** Thermal speed scales with the square root of absolute temperature. */
export function speedFactor(temperatureC: number): number {
  return Math.sqrt(Math.max(temperatureC + KELVIN_OFFSET, 1) / REFERENCE_TEMPERATURE_K);
}

export function particleCounts(fractions: StateFractions, total = DEFAULT_PARTICLE_COUNT) {
  const solid = Math.round(total * fractions.solid);
  const gas = Math.round(total * fractions.gas);
  return { solid, gas, liquid: Math.max(0, total - solid - gas) };
}

export function buildParticleLayout(
  fractions: StateFractions,
  total = DEFAULT_PARTICLE_COUNT,
  seed = 7,
): ModelParticle[] {
  const random = seededRandom(seed);
  const counts = particleCounts(fractions, total);
  const particles: ModelParticle[] = [];
  const floor = PARTICLE_BOX.height - BOX_MARGIN;
  const liquidPerRow = Math.floor((PARTICLE_BOX.width - BOX_MARGIN * 2) / LIQUID_SPACING);
  const liquidRows = Math.ceil(counts.liquid / Math.max(liquidPerRow, 1));

  for (let index = 0; index < counts.liquid; index += 1) {
    const row = Math.floor(index / liquidPerRow);
    const column = index % liquidPerRow;
    const angle = random() * Math.PI * 2;
    particles.push({
      id: particles.length,
      state: 'liquid',
      homeX: BOX_MARGIN + LIQUID_SPACING / 2 + column * LIQUID_SPACING + (row % 2) * (LIQUID_SPACING / 2) + (random() - 0.5) * 1.6,
      homeY: floor - row * (LIQUID_SPACING * 0.88) + (random() - 0.5) * 1.4,
      directionX: Math.cos(angle) >= 0 ? 1 : -1,
      directionY: 0,
      phaseOffset: random() * Math.PI * 2,
    });
  }

  // The solid lattice sits on the floor, or on top of any liquid (ice floats on water).
  const latticeFloor = floor - liquidRows * (LIQUID_SPACING * 0.88);
  const latticeLeft = (PARTICLE_BOX.width - (LATTICE_COLUMNS - 1) * LATTICE_SPACING) / 2;
  for (let index = 0; index < counts.solid; index += 1) {
    const row = Math.floor(index / LATTICE_COLUMNS);
    const column = index % LATTICE_COLUMNS;
    particles.push({
      id: particles.length,
      state: 'solid',
      homeX: latticeLeft + column * LATTICE_SPACING,
      homeY: latticeFloor - row * LATTICE_SPACING,
      directionX: 0,
      directionY: 0,
      phaseOffset: random() * Math.PI * 2,
    });
  }

  for (let index = 0; index < counts.gas; index += 1) {
    const angle = index * GOLDEN_ANGLE + random() * 0.5;
    particles.push({
      id: particles.length,
      state: 'gas',
      homeX: BOX_MARGIN + random() * (PARTICLE_BOX.width - BOX_MARGIN * 2),
      homeY: BOX_MARGIN + random() * (PARTICLE_BOX.height * 0.55),
      directionX: Math.cos(angle),
      directionY: Math.sin(angle),
      phaseOffset: random() * Math.PI * 2,
    });
  }
  return particles;
}

/** Folds a straight-line position back into [min, max] as if it bounced off the walls. */
export function reflectIntoRange(value: number, min: number, max: number): number {
  const span = max - min;
  if (span <= 0) return min;
  const period = span * 2;
  const wrapped = (((value - min) % period) + period) % period;
  return min + (wrapped <= span ? wrapped : period - wrapped);
}

/** Particle positions `timeS` seconds into the animation. Time 0 is the still picture. */
export function particlePositionsAt(
  particles: ModelParticle[],
  timeS: number,
  temperatureC: number,
): ParticlePoint[] {
  const speed = speedFactor(temperatureC);
  const low = BOX_MARGIN;
  const highX = PARTICLE_BOX.width - BOX_MARGIN;
  const highY = PARTICLE_BOX.height - BOX_MARGIN;
  return particles.map((particle) => {
    const wave = SOLID_VIBRATION_HZ * Math.PI * 2 * timeS + particle.phaseOffset;
    if (particle.state === 'solid') {
      const amplitude = SOLID_VIBRATION_AMPLITUDE * speed;
      return {
        id: particle.id,
        state: particle.state,
        x: particle.homeX + amplitude * Math.sin(wave),
        y: particle.homeY + amplitude * Math.cos(wave * 1.3),
      };
    }
    if (particle.state === 'liquid') {
      const drift = particle.directionX * LIQUID_DRIFT_SPEED * speed * timeS;
      return {
        id: particle.id,
        state: particle.state,
        x: reflectIntoRange(particle.homeX + drift + LIQUID_WOBBLE_AMPLITUDE * Math.sin(wave * 0.4), low, highX),
        y: Math.min(highY, particle.homeY + LIQUID_WOBBLE_AMPLITUDE * Math.cos(wave * 0.5)),
      };
    }
    const distance = GAS_SPEED * speed * timeS;
    return {
      id: particle.id,
      state: particle.state,
      x: reflectIntoRange(particle.homeX + particle.directionX * distance, low, highX),
      y: reflectIntoRange(particle.homeY + particle.directionY * distance, low, highY),
    };
  });
}

// Diffusion

export const DIFFUSION_MIN_TEMPERATURE_C = 0;
export const DIFFUSION_MAX_TEMPERATURE_C = 100;

/** Typical order of magnitude of a diffusion coefficient near 25 °C, in m²/s. */
export const TYPICAL_DIFFUSION_COEFFICIENT: Record<MatterState, number> = {
  gas: 1e-5,
  liquid: 1e-9,
  solid: 1e-14,
};

/** Viscosity of liquid water (mPa·s) used for the liquid temperature trend. */
export const WATER_VISCOSITY_MPA_S: Array<[number, number]> = [
  [0, 1.792],
  [10, 1.306],
  [20, 1.002],
  [30, 0.797],
  [40, 0.653],
  [50, 0.547],
  [60, 0.467],
  [70, 0.404],
  [80, 0.355],
  [90, 0.315],
  [100, 0.282],
];

/** Exponent for gas diffusion against absolute temperature (Fuller correlation). */
export const GAS_DIFFUSION_TEMPERATURE_EXPONENT = 1.75;
export const DIFFUSION_REFERENCE_C = 20;
const SAME_RATE_TOLERANCE = 0.02;

export interface DiffusionSetup {
  state: MatterState;
  temperatureC: number;
  label: string;
}

export function waterViscosity(temperatureC: number): number {
  const table = WATER_VISCOSITY_MPA_S;
  for (let index = 1; index < table.length; index += 1) {
    const [t1, v1] = table[index];
    const [t0, v0] = table[index - 1];
    if (temperatureC <= t1) return v0 + ((v1 - v0) * (temperatureC - t0)) / (t1 - t0);
  }
  return table[table.length - 1][1];
}

/** Model diffusion coefficient (m²/s) for a small particle; null outside 0–100 °C. */
export function diffusionCoefficient(state: MatterState, temperatureC: number): number | null {
  if (!Number.isFinite(temperatureC)) return null;
  if (temperatureC < DIFFUSION_MIN_TEMPERATURE_C || temperatureC > DIFFUSION_MAX_TEMPERATURE_C) return null;
  const kelvin = temperatureC + KELVIN_OFFSET;
  const referenceKelvin = DIFFUSION_REFERENCE_C + KELVIN_OFFSET;
  const base = TYPICAL_DIFFUSION_COEFFICIENT[state];
  if (state === 'gas') return base * (kelvin / referenceKelvin) ** GAS_DIFFUSION_TEMPERATURE_EXPONENT;
  if (state === 'liquid') {
    // Stokes–Einstein: D is proportional to T / viscosity.
    return base * (kelvin / referenceKelvin) * (waterViscosity(DIFFUSION_REFERENCE_C) / waterViscosity(temperatureC));
  }
  return base * (kelvin / referenceKelvin);
}

export type FasterChoice = 'first' | 'second' | 'same';

export function compareDiffusion(first: DiffusionSetup, second: DiffusionSetup): FasterChoice | null {
  const a = diffusionCoefficient(first.state, first.temperatureC);
  const b = diffusionCoefficient(second.state, second.temperatureC);
  if (a === null || b === null) return null;
  const ratio = a / b;
  if (Math.abs(ratio - 1) <= SAME_RATE_TOLERANCE) return 'same';
  return ratio > 1 ? 'first' : 'second';
}

// Compression in a sealed syringe (constant temperature)

export const START_PRESSURE_KPA = 100;
export const PASCALS_PER_KPA = 1000;
/** Isothermal compressibility in Pa⁻¹: water at 20 °C and iron (bulk modulus about 170 GPa). */
export const COMPRESSIBILITY_PER_PA: Record<Exclude<MatterState, 'gas'>, number> = {
  liquid: 4.6e-10,
  solid: 1 / 170e9,
};

/** Volume after the pressure is multiplied, at constant temperature. Null for unsupported input. */
export function compressedVolumeMl(state: MatterState, startVolumeMl: number, pressureMultiplier: number): number | null {
  if (!Number.isFinite(startVolumeMl) || startVolumeMl <= 0) return null;
  if (!Number.isFinite(pressureMultiplier) || pressureMultiplier < 1) return null;
  if (state === 'gas') return startVolumeMl / pressureMultiplier; // Boyle’s law
  const extraPressurePa = (pressureMultiplier - 1) * START_PRESSURE_KPA * PASCALS_PER_KPA;
  return startVolumeMl * (1 - COMPRESSIBILITY_PER_PA[state] * extraPressurePa);
}

// Density

export interface DensitySubstance {
  id: string;
  name: string;
  formula?: string;
  state: MatterState;
  densityGPerCm3: number;
  conditions: string;
  sourceId: string;
  reviewStatus: typeof PARTICLE_REVIEW_STATUS;
}

const AT_20 = '20 °C, 101.325 kPa';

function substance(
  id: string,
  name: string,
  state: MatterState,
  densityGPerCm3: number,
  conditions = AT_20,
  sourceId = 'crc-density',
  formula?: string,
): DensitySubstance {
  return { id, name, state, densityGPerCm3, conditions, sourceId, formula, reviewStatus: PARTICLE_REVIEW_STATUS };
}

export const DENSITY_SUBSTANCES: DensitySubstance[] = [
  substance('air', 'Dry air', 'gas', 0.0012, AT_20, 'standard-air'),
  substance('steam', 'Steam (water vapour)', 'gas', 0.000598, '100 °C, 101.325 kPa', 'nist-water', 'H₂O'),
  substance('ethanol', 'Ethanol', 'liquid', 0.789, AT_20, 'crc-density', 'C₂H₅OH'),
  substance('ice', 'Ice', 'solid', 0.917, '0 °C, 101.325 kPa', 'crc-density', 'H₂O'),
  substance('water', 'Liquid water', 'liquid', 0.998, AT_20, 'crc-density', 'H₂O'),
  substance('glycerol', 'Glycerol', 'liquid', 1.26, AT_20, 'crc-density', 'C₃H₈O₃'),
  substance('aluminium', 'Aluminium', 'solid', 2.7, AT_20, 'crc-density', 'Al'),
  substance('iron', 'Iron', 'solid', 7.87, AT_20, 'crc-density', 'Fe'),
  substance('copper', 'Copper', 'solid', 8.96, AT_20, 'crc-density', 'Cu'),
  substance('lead', 'Lead', 'solid', 11.34, AT_20, 'crc-density', 'Pb'),
  substance('mercury', 'Mercury', 'liquid', 13.55, AT_20, 'crc-density', 'Hg'),
  substance('gold', 'Gold', 'solid', 19.32, AT_20, 'crc-density', 'Au'),
];

export const IDENTIFY_TOLERANCE = 0.02;

export function densityOf(id: string): DensitySubstance | null {
  return DENSITY_SUBSTANCES.find((candidate) => candidate.id === id) ?? null;
}

export function calculateDensity(massG: number, volumeCm3: number): number | null {
  if (!Number.isFinite(massG) || !Number.isFinite(volumeCm3) || massG <= 0 || volumeCm3 <= 0) return null;
  return massG / volumeCm3;
}

/** The listed substance within 2% of the density, or null when no substance matches. */
export function identifyByDensity(densityGPerCm3: number): DensitySubstance | null {
  if (!Number.isFinite(densityGPerCm3) || densityGPerCm3 <= 0) return null;
  const matches = DENSITY_SUBSTANCES.filter(
    (candidate) => Math.abs(candidate.densityGPerCm3 - densityGPerCm3) / candidate.densityGPerCm3 <= IDENTIFY_TOLERANCE,
  );
  return matches.length === 1 ? matches[0] : null;
}

/** Whether a substance floats in liquid water at 20 °C. Null for an unknown id. */
export function floatsInWater(id: string): boolean | null {
  const item = densityOf(id);
  const water = densityOf('water');
  if (!item || !water) return null;
  return item.densityGPerCm3 < water.densityGPerCm3;
}

// Prediction cases

export type CaseVisual =
  | { kind: 'states'; states: MatterState[] }
  | { kind: 'heating'; energyJ: number }
  | { kind: 'diffusion'; first: DiffusionSetup; second: DiffusionSetup }
  | { kind: 'compression'; startVolumeMl: number; pressureMultiplier: number }
  | { kind: 'density'; substanceIds: string[]; massG?: number; volumeCm3?: number };

/**
 * How the expected answer is checked against the model. `concept` answers are reviewed text; the
 * other rules recompute the answer from the model so the case can never drift from it.
 */
export type AnswerRule =
  | 'concept'
  | 'more-compressible'
  | 'heating-trend'
  | 'faster-diffusion'
  | 'identify-density'
  | 'float-or-sink'
  | 'greater-mass';

export interface CaseOption {
  id: string;
  label: string;
  /** Specific feedback shown when the learner chooses this option. */
  feedback: string;
}

export interface ParticleCase {
  id: string;
  mode: ParticleMode;
  title: string;
  prompt: string;
  options: CaseOption[];
  answerId: string;
  rule: AnswerRule;
  explanation: string;
  visual: CaseVisual;
  reviewStatus: typeof PARTICLE_REVIEW_STATUS;
}

const HALF = 0.5;
const ROOM_WATER: DiffusionSetup = { state: 'liquid', temperatureC: 20, label: 'Water at 20 °C' };
const HOT_WATER: DiffusionSetup = { state: 'liquid', temperatureC: 80, label: 'Water at 80 °C' };
const ROOM_AIR: DiffusionSetup = { state: 'gas', temperatureC: 20, label: 'Still air at 20 °C' };

function option(id: string, label: string, feedback: string): CaseOption {
  return { id, label, feedback };
}

function particleCase(item: Omit<ParticleCase, 'reviewStatus'>): ParticleCase {
  return { ...item, reviewStatus: PARTICLE_REVIEW_STATUS };
}

export const PARTICLE_CASES: ParticleCase[] = [
  // ----- Year 7 · States -----------------------------------------------------------------------
  particleCase({
    id: 'fixed-pattern',
    mode: 'states',
    title: 'Which state has a fixed pattern?',
    prompt: 'In which state are particles packed in a fixed, regular pattern and only vibrate on the spot?',
    options: [
      option('solid', 'Solid', 'Yes. Strong attractions hold solid particles in fixed places, so they can only vibrate.'),
      option('liquid', 'Liquid', 'Liquid particles are close together, but they are not fixed. They slide past one another, which is why a liquid can be poured.'),
      option('gas', 'Gas', 'Gas particles are far apart and fly around freely. Nothing holds them in a pattern.'),
    ],
    answerId: 'solid',
    rule: 'concept',
    explanation: 'Compare the three boxes: only the solid keeps every particle in the same place in a regular pattern.',
    visual: { kind: 'states', states: ['solid', 'liquid', 'gas'] },
  }),
  particleCase({
    id: 'pour-into-bowl',
    mode: 'states',
    title: 'Pour water into a wide bowl',
    prompt: '200 mL of water is poured from a tall glass into a wide bowl. What happens to its volume?',
    options: [
      option('same', 'It stays 200 mL', 'Yes. The particles stay close together, so the volume stays the same. Only the shape changes to fit the bowl.'),
      option('more', 'It increases', 'The water looks spread out, but the particles do not move further apart. A liquid keeps its volume.'),
      option('less', 'It decreases', 'No particles are lost and they do not squash closer together. The liquid keeps its volume.'),
    ],
    answerId: 'same',
    rule: 'concept',
    explanation: 'Liquid particles can slide past one another (so the shape changes) but stay close together (so the volume stays the same).',
    visual: { kind: 'states', states: ['liquid'] },
  }),
  particleCase({
    id: 'squash-syringes',
    mode: 'states',
    title: 'Squash two sealed syringes',
    prompt: 'Two sealed syringes each hold 50 mL: one of air, one of water. You double the pressure on each plunger. Which one gets noticeably smaller?',
    options: [
      option('gas', 'The air', 'Yes. Gas particles have large spaces between them, so pushing them closer together halves the volume to 25 mL.'),
      option('liquid', 'The water', 'Water particles are already touching. Doubling the pressure changes the volume by only about 0.002 mL — far too little to see.'),
      option('same', 'Both shrink by the same amount', 'The two states behave very differently. Only the gas has empty space between particles that can be squeezed out.'),
    ],
    answerId: 'gas',
    rule: 'more-compressible',
    explanation: 'Gas: 50 mL → 25 mL. Water: 50 mL → about 49.998 mL. Only gases are easy to compress.',
    visual: { kind: 'compression', startVolumeMl: 50, pressureMultiplier: 2 },
  }),
  particleCase({
    id: 'thermometer-melting',
    mode: 'states',
    title: 'Heat ice while it melts',
    prompt: 'Ice at 0 °C is heated steadily. What does the thermometer show until all the ice has melted?',
    options: [
      option('constant', 'It stays at 0 °C', 'Yes. While ice melts, the energy goes into overcoming the attractions that hold the particles in place, so the temperature stays flat.'),
      option('rises', 'It rises steadily', 'The temperature rises before and after melting, but not during it. Find the flat section on the heating curve.'),
      option('falls', 'It falls', 'Heating adds energy, so the temperature cannot fall. It stays flat while the state changes.'),
    ],
    answerId: 'constant',
    rule: 'heating-trend',
    explanation: 'The heating curve has a flat section (a plateau) at 0 °C while ice and water are both present.',
    visual: { kind: 'heating', energyJ: energyWithinPhase('melting', HALF) },
  }),
  particleCase({
    id: 'keep-boiling',
    mode: 'states',
    title: 'Keep heating boiling water',
    prompt: 'Water is boiling at 100 °C at sea-level pressure. You keep heating it. What happens to the temperature of the boiling water?',
    options: [
      option('constant', 'It stays at 100 °C', 'Yes. The extra energy turns more liquid into gas instead of raising the temperature.'),
      option('rises', 'It rises above 100 °C', 'A hotter flame makes it boil faster, not hotter. The liquid stays at 100 °C until it has all boiled away.'),
      option('falls', 'It falls below 100 °C', 'Heating adds energy, so the temperature does not fall while it boils.'),
    ],
    answerId: 'constant',
    rule: 'heating-trend',
    explanation: 'The second plateau on the heating curve is at 100 °C. The temperature rises again only when all the water is steam.',
    visual: { kind: 'heating', energyJ: energyWithinPhase('boiling', HALF) },
  }),
  particleCase({
    id: 'steam-on-window',
    mode: 'states',
    title: 'Steam meets a cold window',
    prompt: 'Steam from a shower touches a cold window and droplets form. Which change of state is this?',
    options: [
      option('condensation', 'Condensation', 'Yes. The gas particles lose energy, slow down and attract one another into a liquid.'),
      option('evaporation', 'Evaporation', 'Evaporation is liquid changing to gas. Here gas is changing to liquid, the reverse.'),
      option('melting', 'Melting', 'Melting is solid changing to liquid. There is no solid here.'),
      option('freezing', 'Freezing', 'Freezing makes a solid. The droplets are liquid water.'),
    ],
    answerId: 'condensation',
    rule: 'concept',
    explanation: 'Cooling a gas lets attractions pull the particles close together: gas → liquid is condensation.',
    visual: { kind: 'states', states: ['gas', 'liquid'] },
  }),
  particleCase({
    id: 'dye-hot-cold',
    mode: 'states',
    title: 'Food colouring in hot and cold water',
    prompt: 'One drop of food colouring goes into still water at 20 °C and another into still water at 80 °C. Where does the colour spread faster?',
    options: [
      option('first', 'Water at 20 °C', 'Cold particles move more slowly, so the colour spreads more slowly. Compare the two boxes.'),
      option('second', 'Water at 80 °C', 'Yes. Particles in hot water move faster, so the dye particles are bumped and spread out sooner.'),
      option('same', 'About the same', 'Temperature changes how fast particles move, so the spreading rate is different.'),
    ],
    answerId: 'second',
    rule: 'faster-diffusion',
    explanation: 'In this model, diffusion in water at 80 °C is about 3.4 times faster than at 20 °C.',
    visual: { kind: 'diffusion', first: ROOM_WATER, second: HOT_WATER },
  }),
  particleCase({
    id: 'perfume-or-dye',
    mode: 'states',
    title: 'Perfume in air or dye in water?',
    prompt: 'Both at 20 °C and with no stirring or draughts: which spreads faster by diffusion alone — perfume in still air or dye in still water?',
    options: [
      option('first', 'Perfume in air', 'Yes. Gas particles are far apart and move fast, so perfume particles travel further between collisions.'),
      option('second', 'Dye in water', 'Liquid particles are crowded, so a dye particle collides constantly and spreads slowly.'),
      option('same', 'About the same', 'The state matters a lot. Diffusion in a gas is roughly ten thousand times faster than in a liquid.'),
    ],
    answerId: 'first',
    rule: 'faster-diffusion',
    explanation: 'Typical diffusion coefficients: about 10⁻⁵ m²/s in a gas and about 10⁻⁹ m²/s in a liquid.',
    visual: { kind: 'diffusion', first: ROOM_AIR, second: ROOM_WATER },
  }),
  particleCase({
    id: 'mystery-block',
    mode: 'states',
    title: 'Identify a metal block',
    prompt: 'A metal block has a mass of 81.0 g and a volume of 30.0 cm³. Which metal could it be?',
    options: [
      option('aluminium', 'Aluminium (2.70 g/cm³)', 'Yes. Density = mass ÷ volume = 81.0 ÷ 30.0 = 2.70 g/cm³, which matches aluminium.'),
      option('iron', 'Iron (7.87 g/cm³)', 'Calculate density = mass ÷ volume: 81.0 ÷ 30.0 = 2.70 g/cm³. Iron is almost three times denser.'),
      option('gold', 'Gold (19.32 g/cm³)', '30 cm³ of gold would have a mass of about 580 g. Calculate mass ÷ volume to check.'),
    ],
    answerId: 'aluminium',
    rule: 'identify-density',
    explanation: 'Density = mass ÷ volume = 81.0 g ÷ 30.0 cm³ = 2.70 g/cm³.',
    visual: { kind: 'density', substanceIds: ['aluminium', 'iron', 'gold'], massG: 81, volumeCm3: 30 },
  }),
  particleCase({
    id: 'ice-in-water',
    mode: 'states',
    title: 'Does ice float?',
    prompt: 'An ice cube is dropped into a glass of liquid water. Will it float or sink?',
    options: [
      option('floats', 'It floats', 'Yes. Ice (0.917 g/cm³) is less dense than liquid water (0.998 g/cm³), so it floats.'),
      option('sinks', 'It sinks', 'Most solids are denser than their liquids, but ice is unusual: 0.917 g/cm³ is less than water’s 0.998 g/cm³.'),
    ],
    answerId: 'floats',
    rule: 'float-or-sink',
    explanation: 'A substance floats in water when its density is less than the density of the water.',
    visual: { kind: 'density', substanceIds: ['ice', 'water'] },
  }),
  particleCase({
    id: 'gold-or-aluminium',
    mode: 'states',
    title: 'Same size, different mass',
    prompt: 'A 10.0 cm³ cube of gold and a 10.0 cm³ cube of aluminium are the same size. Which has the greater mass?',
    options: [
      option('gold', 'The gold cube', 'Yes. Mass = density × volume: gold 19.32 × 10.0 ≈ 193 g; aluminium 2.70 × 10.0 = 27.0 g.'),
      option('aluminium', 'The aluminium cube', 'Same volume means the denser material has more mass. Gold is about seven times denser than aluminium.'),
      option('same', 'They are the same', 'Equal volumes only have equal masses when the densities are equal.'),
    ],
    answerId: 'gold',
    rule: 'greater-mass',
    explanation: 'For equal volumes, mass is proportional to density.',
    visual: { kind: 'density', substanceIds: ['gold', 'aluminium'], volumeCm3: 10 },
  }),

  // ----- Year 8 · Explain ----------------------------------------------------------------------
  particleCase({
    id: 'where-energy-goes',
    mode: 'explain',
    title: 'Where does the energy go?',
    prompt: 'While ice melts at 0 °C, energy is still being added but the temperature does not rise. Where does the energy go?',
    options: [
      option('attractions', 'It overcomes the attractions holding particles in place', 'Yes. The energy lets particles break free of their fixed places, so it does not raise their average speed.'),
      option('stop', 'The particles stop moving', 'Particles never stop moving while energy is added. They keep vibrating at the same average speed.'),
      option('lost', 'The energy is lost to the room', 'Some energy can escape, but the flat section appears even in a perfectly insulated container. The energy is doing work on attractions.'),
      option('bigger', 'The particles get bigger', 'Particles do not change size. The arrangement changes, not the particles themselves.'),
    ],
    answerId: 'attractions',
    rule: 'concept',
    explanation: 'On a plateau, added energy overcomes attractions between particles instead of increasing their kinetic energy.',
    visual: { kind: 'heating', energyJ: energyWithinPhase('melting', HALF) },
  }),
  particleCase({
    id: 'longer-boiling',
    mode: 'explain',
    title: 'Why is boiling so long?',
    prompt: 'For 1 g of water, melting needs 334 J but boiling needs about 2260 J. Why is the boiling plateau so much longer?',
    options: [
      option('separate', 'Boiling must separate the particles almost completely', 'Yes. Melting only loosens the arrangement; boiling must overcome nearly all of the attractions so particles can move far apart.'),
      option('break-molecules', 'Boiling breaks water into hydrogen and oxygen atoms', 'The particles in steam are still whole H₂O molecules. Boiling separates molecules from each other; it does not break them apart.'),
      option('hotter', 'Boiling water is hotter than melting ice', 'The plateau length is energy, not temperature. Both plateaus are flat; boiling simply needs more energy per gram.'),
      option('bigger', 'Water particles get bigger as they boil', 'Particles stay the same size. The spaces between them grow.'),
    ],
    answerId: 'separate',
    rule: 'concept',
    explanation: 'The plateau lengths on the heating curve are the energies needed to change state: 334 J/g to melt and 2260 J/g to boil.',
    visual: { kind: 'heating', energyJ: energyWithinPhase('boiling', HALF) },
  }),
  particleCase({
    id: 'why-gas-squashes',
    mode: 'explain',
    title: 'Why does air squash?',
    prompt: 'Why can the air in a sealed syringe be compressed, but the water cannot noticeably?',
    options: [
      option('spaces', 'Gas particles have large spaces between them', 'Yes. Compressing a gas pushes the particles into the empty space between them. Liquid particles are already touching.'),
      option('soft', 'Gas particles are soft and shrink', 'Particles do not shrink. Only the spaces between them get smaller.'),
      option('heavy', 'Water particles are heavier', 'Mass does not decide compressibility. Spacing between the particles does.'),
      option('no-particles', 'Air has no particles', 'Air is made of particles (mostly N₂ and O₂ molecules). They are just far apart.'),
    ],
    answerId: 'spaces',
    rule: 'concept',
    explanation: 'Doubling the pressure halves the gas volume (50 mL → 25 mL) but changes water by only about 0.002 mL.',
    visual: { kind: 'compression', startVolumeMl: 50, pressureMultiplier: 2 },
  }),
  particleCase({
    id: 'hot-rail',
    mode: 'explain',
    title: 'Why is a hot rail longer?',
    prompt: 'A steel rail is slightly longer on a hot day. What happens to its particles?',
    options: [
      option('vibrate', 'They vibrate more and take up a little more space', 'Yes. Faster vibrations push neighbouring particles slightly further apart, so the solid expands.'),
      option('expand', 'Each particle expands', 'Particles do not grow. The average distance between them increases.'),
      option('more', 'More particles are made', 'Heating does not create particles. The same particles take up more space.'),
      option('melt', 'The rail starts to melt', 'Steel melts at well over 1000 °C. A hot day only makes the particles vibrate more.'),
    ],
    answerId: 'vibrate',
    rule: 'concept',
    explanation: 'Thermal expansion: particles stay in their places but vibrate further, increasing the average spacing.',
    visual: { kind: 'states', states: ['solid'] },
  }),
  particleCase({
    id: 'why-hot-diffuses',
    mode: 'explain',
    title: 'Why does heat speed up spreading?',
    prompt: 'Dye spreads faster in water at 80 °C than at 20 °C. Which explanation fits the particle model?',
    options: [
      option('faster', 'Particles move faster on average when hotter', 'Yes. Faster water and dye particles collide more energetically, so the dye particles move away from the drop sooner.'),
      option('more-particles', 'Hot water has more particles', 'The amount of water is the same. The particles move faster; there are not more of them.'),
      option('smaller', 'Dye particles shrink in hot water', 'Particle size does not change with temperature in this model.'),
      option('push', 'Heat pushes the dye upwards', 'Diffusion happens in all directions. In this comparison there is no stirring or current, only random particle motion.'),
    ],
    answerId: 'faster',
    rule: 'concept',
    explanation: 'Higher temperature means higher average kinetic energy, so diffusion is faster.',
    visual: { kind: 'diffusion', first: ROOM_WATER, second: HOT_WATER },
  }),
  particleCase({
    id: 'gas-fills-room',
    mode: 'explain',
    title: 'Why does a gas fill its container?',
    prompt: 'A small amount of gas released into an empty sealed box spreads through the whole box. Why?',
    options: [
      option('free', 'Particles move fast in all directions and attractions are very weak', 'Yes. Nothing holds gas particles together, so they keep moving until they meet a wall.'),
      option('expand', 'Each gas particle expands to fill the space', 'Gas particles do not change size. They spread out, leaving large empty spaces.'),
      option('rise', 'Gases are lighter than air, so they rise', 'Gas also fills the bottom corners of the box. Spreading is caused by particle motion in every direction.'),
      option('repel', 'Gas particles strongly repel each other', 'Gas particles attract one another only weakly and do not push each other apart. Their speed is enough to spread them out.'),
    ],
    answerId: 'free',
    rule: 'concept',
    explanation: 'Gas particles travel in straight lines until they collide, so they reach every part of the container.',
    visual: { kind: 'states', states: ['gas'] },
  }),
  particleCase({
    id: 'ice-less-dense',
    mode: 'explain',
    title: 'Why is ice less dense than water?',
    prompt: 'Ice (0.917 g/cm³) floats on liquid water (0.998 g/cm³). Using the particle model, why is ice less dense?',
    options: [
      option('open', 'Water particles in ice are held in an open arrangement with more space', 'Yes. The same mass of particles takes up more volume in ice, so its density is lower. Water is unusual in this.'),
      option('lighter', 'Ice particles are lighter than water particles', 'Ice and liquid water are both made of H₂O molecules with the same mass. The spacing differs.'),
      option('air', 'Ice always contains trapped air', 'Clear, bubble-free ice still floats. The reason is the arrangement of the water molecules.'),
      option('smaller', 'Ice particles are smaller', 'Particles do not change size when water freezes. Their arrangement changes.'),
    ],
    answerId: 'open',
    rule: 'concept',
    explanation: 'Density = mass ÷ volume. Same mass, more volume in ice → lower density.',
    visual: { kind: 'density', substanceIds: ['ice', 'water'] },
  }),
  particleCase({
    id: 'steam-burn',
    mode: 'explain',
    title: 'Why can steam burn badly?',
    prompt: 'Steam at 100 °C can cause a worse burn than the same mass of water at 100 °C. Why?',
    options: [
      option('condense', 'As steam condenses on skin, attractions re-form and release extra energy', 'Yes. Condensing 1 g of steam releases about 2260 J before the water even starts to cool.'),
      option('hotter', 'Steam particles are hotter', 'Both are at 100 °C. The difference is the extra energy released when the gas changes back to liquid.'),
      option('different', 'Steam is a different substance', 'Steam and liquid water are both H₂O. Changing state does not make a new substance.'),
      option('more', 'Steam has more particles per gram', 'One gram of steam and one gram of water contain the same number of H₂O molecules.'),
    ],
    answerId: 'condense',
    rule: 'concept',
    explanation: 'Condensing is the reverse of boiling: the energy used to separate the particles is released when they come back together.',
    visual: { kind: 'heating', energyJ: energyWithinPhase('boiling', HALF) },
  }),
  particleCase({
    id: 'sweat-cools',
    mode: 'explain',
    title: 'Why does evaporation cool you?',
    prompt: 'Sweat evaporating from your skin cools you down. Which explanation fits the particle model?',
    options: [
      option('fastest', 'The fastest particles escape, so the average energy of those left falls', 'Yes. Only particles with enough energy break free of the liquid, which lowers the average energy of the remaining liquid.'),
      option('cold-particles', 'Evaporation adds cold particles to your skin', 'Nothing cold is added. Energy is carried away by the escaping particles.'),
      option('colder', 'Sweat is colder than skin', 'Sweat starts at skin temperature. It cools you by taking energy as it evaporates.'),
      option('stop', 'Skin particles stop moving', 'Particles do not stop moving. They move a little slower on average as energy leaves.'),
    ],
    answerId: 'fastest',
    rule: 'concept',
    explanation: 'Evaporation happens at the surface below the boiling point, and it removes the most energetic particles.',
    visual: { kind: 'states', states: ['liquid', 'gas'] },
  }),
  particleCase({
    id: 'vapour-density',
    mode: 'explain',
    title: 'Same particles, different density',
    prompt: 'Steam at 100 °C (about 0.0006 g/cm³) is far less dense than liquid water, yet both are H₂O. Why?',
    options: [
      option('spread', 'The same particles are spread through a much larger volume', 'Yes. 1 g of water takes about 1 cm³ as a liquid but about 1700 cm³ as steam at 100 °C.'),
      option('lighter', 'Steam particles have less mass', 'Each H₂O molecule has the same mass in every state.'),
      option('different', 'Steam is made of different molecules', 'Steam is H₂O, the same molecules as liquid water.'),
      option('smaller', 'Steam particles are smaller', 'Particle size does not change. The spaces between particles grow enormously.'),
    ],
    answerId: 'spread',
    rule: 'concept',
    explanation: 'Gas density is low because the particles occupy a tiny fraction of the space.',
    visual: { kind: 'density', substanceIds: ['steam', 'water'] },
  }),
];

export function casesForMode(mode: ParticleMode): ParticleCase[] {
  return PARTICLE_CASES.filter((item) => item.mode === mode);
}

/** The answer recomputed from the model, or null for a reviewed concept answer. */
export function deriveModelAnswer(item: ParticleCase): string | null {
  const { visual } = item;
  switch (item.rule) {
    case 'more-compressible': {
      if (visual.kind !== 'compression') return null;
      const states: MatterState[] = ['gas', 'liquid'];
      const volumes = states.map((state) => compressedVolumeMl(state, visual.startVolumeMl, visual.pressureMultiplier));
      if (volumes.some((volume) => volume === null)) return null;
      return (volumes[0] as number) < (volumes[1] as number) ? 'gas' : 'liquid';
    }
    case 'heating-trend': {
      if (visual.kind !== 'heating') return null;
      const state = heatingStateAt(visual.energyJ);
      if (!state) return null;
      return state.plateau ? 'constant' : 'rises';
    }
    case 'faster-diffusion':
      return visual.kind === 'diffusion' ? compareDiffusion(visual.first, visual.second) : null;
    case 'identify-density': {
      if (visual.kind !== 'density' || visual.massG === undefined || visual.volumeCm3 === undefined) return null;
      const density = calculateDensity(visual.massG, visual.volumeCm3);
      return density === null ? null : identifyByDensity(density)?.id ?? null;
    }
    case 'float-or-sink': {
      if (visual.kind !== 'density') return null;
      const floats = floatsInWater(visual.substanceIds[0]);
      return floats === null ? null : floats ? 'floats' : 'sinks';
    }
    case 'greater-mass': {
      if (visual.kind !== 'density' || visual.substanceIds.length !== 2) return null;
      const [a, b] = visual.substanceIds.map(densityOf);
      if (!a || !b) return null;
      if (a.densityGPerCm3 === b.densityGPerCm3) return 'same';
      return a.densityGPerCm3 > b.densityGPerCm3 ? a.id : b.id;
    }
    default:
      return null;
  }
}

export interface ParticleCaseResult {
  correct: boolean;
  chosen: CaseOption;
  expected: CaseOption;
  explanation: string;
}

/** Checks a prediction. Returns null for an option that does not belong to the case (fail closed). */
export function checkParticleCase(item: ParticleCase, optionId: string): ParticleCaseResult | null {
  const chosen = item.options.find((option) => option.id === optionId);
  const expected = item.options.find((option) => option.id === item.answerId);
  if (!chosen || !expected) return null;
  return { correct: chosen.id === expected.id, chosen, expected, explanation: item.explanation };
}
